import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AppShell, Icon } from '../components/AppShell';
import { api, ApiError, type BouwflowUploadAntwoord } from '../lib/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../lib/auth';
import { fmtEUR, fmtNum, fmtDate, fmtDateTime, statusChipClass, statusLabel, normalizeProjectNummer } from '../lib/format';
import { BouwflowLink } from '../components/BouwflowLink';
import { PricelistItemPicker } from '../components/PricelistItemPicker';
import { PdfViewerPanel } from '../components/PdfViewerPanel';
import { GevelBlock } from '../components/GevelBlock';
import { GevelVariantModal } from '../components/GevelVariantModal';
import { ExtraRegelsSection } from '../components/ExtraRegelsSection';
import { GelinkteProjectenEffect, PctWaarde, useLivePercentages } from '../components/LivePercentage';
import { ProjectNiveauKaart } from '../components/ProjectNiveau';
import { OverlapVragen, beslisOverlap } from '../components/OverlapVragen';
import { TweePercentages } from '../components/TweePercentages';
import { DossierPaneel } from '../components/DossierPaneel';
import { WorkflowKenmerken, WorkflowMeldingen } from '../components/WorkflowKenmerken';
import type { NotitieFocus } from '../components/Notities';
import {
  OpenPostenBlok,
  OpleveringChip,
  OpleveringKeuze,
  PrijsafwijkingenBlok,
  VersiesPaneel,
  VerschuifKnoppen,
  afwijkingenZonderReden,
  useLiveAfwijkingen,
  verschuif,
  zetReden,
} from '../components/CalculatieExtras';
import { useOverlapSync } from '../lib/overlap';
import {
  computeTotals,
  runMatching,
  learningToMatchingOptions,
  lineSubtotaal,
  isGevelCalculatie,
  valideerExtraRegel,
  berekenSchijven,
  USER_SKIP_REDEN,
  type ParsedItem,
  type MatchedLine,
  type MatchedSubItem,
  type MeerwerkLine,
  type SkippedItem,
  type ExtraRegel,
  type GevelConfig,
  type Pricelist,
  type PricelistItem,
} from '@recotex/shared';

function uidLocal(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
}

interface RematchItem {
  parsedItemId: string;
  pdfNaam: string;
  hoeveelheid: number;
  eenheid: string;
  originLineId: string;
}

type PendingItem = { parsedItem: ParsedItem; suggestions: { item: PricelistItem; score: number }[]; kind: 'review' | 'unmatched' };

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Pop-ups bevestigen: enkel Eranda, Timen, Ervin of Jordy (SPEC_wijzigingen 6).
  const magBevestigen = !!user?.magBevestigen;

  const q = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.projects.detail(id!),
    enabled: !!id,
  });
  // Vanuit "Te doen" of de mail: /project/:id#open-posten scrolt naar de open posten (6.4).
  const location = useLocation();
  useEffect(() => {
    if (!q.data || !location.hash) return;
    const t = window.setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => window.clearTimeout(t);
  }, [q.data, location.hash]);

  // Edit-state — geactiveerd via Bewerken knop. Concept-projecten openen direct in edit-mode
  // de eerste keer; na expliciete Opslaan blijven we in read-mode (anders kan je nooit bij Goedkeuren).
  const [editing, setEditing] = useState(false);
  const autoEditedRef = useRef(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  // Knoppen bovenaan → dossierpaneel: naar het notitieveld, de collegalijst of het labelveld.
  const [dossierFocus, setDossierFocus] = useState<NotitieFocus | null>(null);
  const [labelSignaal, setLabelSignaal] = useState(0);
  const [showEmail, setShowEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [emailCcMe, setEmailCcMe] = useState(true);
  const [emailCcBoekhouding, setEmailCcBoekhouding] = useState(false);
  const [meta, setMeta] = useState({
    klantNaam: '', klantAdres: '', adviseur: '', projectTitel: '', projectNummer: '', opmerkingen: '',
  });
  const [matchedLines, setMatchedLines] = useState<MatchedLine[]>([]);
  const [meerwerkLines, setMeerwerkLines] = useState<MeerwerkLine[]>([]);
  const [extraRegels, setExtraRegels] = useState<ExtraRegel[]>([]);
  const [gevel, setGevel] = useState<GevelConfig | null>(null);
  const [gevelModal, setGevelModal] = useState<null | 'kies' | 'verversen'>(null);
  const gevelModalAutoRef = useRef(false);
  const [toonFouten, setToonFouten] = useState(false);
  /** Bewerken van een verstuurde calculatie maakt een nieuwe versie (SPEC_workflow 5.1). */
  const [nieuweVersieModus, setNieuweVersieModus] = useState(false);
  const [skippedItems, setSkippedItems] = useState<SkippedItem[]>([]);
  const [offertewaarde, setOffertewaarde] = useState<number | null>(null);
  const [ploegId, setPloegId] = useState('');
  const [rematchQueue, setRematchQueue] = useState<RematchItem[]>([]);

  // Deep edit: hermatchen modus
  const [deepEdit, setDeepEdit] = useState(false);
  const [editedParsedItems, setEditedParsedItems] = useState<ParsedItem[]>([]);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);

  // Hydrate edit-state vanuit project + auto-edit eenmalig voor concept
  useEffect(() => {
    if (!q.data) return;
    const p = q.data.project;
    setMeta({
      klantNaam: p.klantNaam,
      klantAdres: p.klantAdres,
      adviseur: p.adviseur,
      projectTitel: p.projectTitel,
      projectNummer: (p as { projectNummer?: string }).projectNummer ?? '',
      opmerkingen: (p as { opmerkingen?: string }).opmerkingen ?? '',
    });
    setMatchedLines(p.matchedLines || []);
    setMeerwerkLines(p.meerwerkLines || []);
    setExtraRegels(p.extraRegels || []);
    setGevel(p.gevel ?? null);
    setToonFouten(false);
    setSkippedItems(p.skippedItems || []);
    setOffertewaarde(p.offertewaarde ?? null);
    setPloegId(p.ploegId);
    setEditedParsedItems(p.parsedItems || []);
    if (p.status === 'draft' && !p.verstuurd && !autoEditedRef.current) {
      setEditing(true);
      autoEditedRef.current = true;
    }
    // Pop-up A/B bij het eerste openen van een gevelcalculatie zonder bevestigde keuze (1.3).
    if (p.gevel && !p.gevel.bevestigd && !gevelModalAutoRef.current && isGevelCalculatie(p.matchedLines || [], p.meerwerkLines || [])) {
      gevelModalAutoRef.current = true;
      setGevelModal('kies');
    }
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prijslijst(versie) van deze calculatie — om handmatig items toe te voegen en te hermatchen.
  // Dat kan een prijslijst per type werken zijn (bv. Nedelco hellend dak), niet per se die van de ploeg.
  const pricelistQ = useQuery({
    queryKey: ['pricelist', q.data?.project?.pricelistSnapshotId],
    queryFn: async () => (await api.pricelists.detail(q.data!.project.pricelistSnapshotId)).pricelist,
    enabled: !!q.data?.project?.pricelistSnapshotId,
  });

  // Geheugen van de matcher voor deze prijslijst — zodat "hermatchen" hetzelfde
  // geheugen raadpleegt als /parse op de server.
  const learningQ = useQuery({
    queryKey: ['learning', pricelistQ.data?.id],
    queryFn: () => api.pricelists.learned(pricelistQ.data!.id),
    enabled: !!pricelistQ.data?.id,
  });

  // Alle ploegen — voor de manuele onderaannemer-override in edit-mode.
  const ploegenQ = useQuery({ queryKey: ['ploegen'], queryFn: () => api.ploegen.list() });
  const allPloegen = ploegenQ.data?.ploegen ?? [];
  const ploegChanged = !!q.data && ploegId !== q.data.project.ploegId;

  const totals = useMemo(
    () => computeTotals({ matchedLines, meerwerkLines, extraRegels, gevel }),
    [matchedLines, meerwerkLines, extraRegels, gevel],
  );
  // Prijsafwijkingen t.o.v. de standaardprijs, live tijdens het bewerken (27okt 2).
  const afwijkingen = useLiveAfwijkingen({
    matchedLines,
    meerwerkLines,
    pricelistItems: pricelistQ.data?.items ?? [],
    gevel,
    opgeslagen: q.data?.project.prijsafwijkingen,
  });
  const isGevel = isGevelCalculatie(matchedLines, meerwerkLines);
  const vervangenIds = useMemo(() => new Set(totals.gevel?.vervangenLijnIds ?? []), [totals.gevel]);
  const openOverlap = matchedLines.filter((l) => l.overlap?.status === 'open').length;
  const gevelNietBevestigd = isGevel && !!gevel && !gevel.bevestigd;
  const blokkeerGoedkeuren = gevelNietBevestigd
    ? 'Bevestig eerst welke gevelprijslijst (A of B) geldt.'
    : openOverlap > 0 ? `Beslis eerst over ${openOverlap} post(en) die normaal al inbegrepen zijn.` : null;

  // Overlapcontrole (5.4) bij elke wijziging van de werkposten tijdens het bewerken.
  useOverlapSync(matchedLines, setMatchedLines, pricelistQ.data?.items ?? [], learningQ.data?.overlaps, editing);

  const gevelKeuze = gevel ? { variant: gevel.variant, pricelistId: gevel.pricelistId, bevestigd: gevel.bevestigd } : undefined;

  // Percentage op projectniveau, live tijdens het bewerken (2.4–2.5).
  const projData = q.data?.project;
  // Referenties uit Bouwflow (aanvaarde offertes, verstuurde facturen); de manuele offertewaarde
  // enkel voor een calculatie zonder Bouwflow-koppeling.
  const refLive = projData?.projectPercentage?.referentie ?? null;
  const gefactureerdLive = refLive ? refLive.gefactureerd : projData?.gefactureerd ?? null;
  const offerteLive = refLive?.offertewaarde ?? (projData?.bouwflowProjectId != null ? null : offertewaarde);
  const live = useLivePercentages({
    calcId: id,
    projectNummer: meta.projectNummer,
    werkpostenEnMeerwerk: totals.subtotaal + totals.meerwerk,
    extraRegels,
    eigenNoemer: gefactureerdLive != null && gefactureerdLive > 0
      ? { noemer: gefactureerdLive, basis: 'gefactureerd' }
      : { noemer: offerteLive, basis: offerteLive != null ? 'offertewaarde' : null },
    verstuurd: !!projData?.verstuurdOp,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      api.projects.update(id!, {
        klantNaam: meta.klantNaam,
        klantAdres: meta.klantAdres,
        adviseur: meta.adviseur,
        projectTitel: meta.projectTitel,
        projectNummer: meta.projectNummer,
        opmerkingen: meta.opmerkingen,
        ploegId,
        parsedItemsJson: JSON.stringify(editedParsedItems),
        matchedLinesJson: JSON.stringify(matchedLines),
        meerwerkLinesJson: JSON.stringify(meerwerkLines),
        skippedItemsJson: JSON.stringify(skippedItems),
        extraRegels,
        ...(gevelKeuze ? { gevel: gevelKeuze } : {}),
        offertewaarde,
        // Conflictregel (27okt 7): de server weet op welke versie deze bewerking gebaseerd is.
        ...(q.data ? { basisVersie: q.data.project.versie } : {}),
        ...(nieuweVersieModus ? { nieuweVersie: true } : {}),
      }),
    onSuccess: (resp) => {
      toast.success(nieuweVersieModus ? `Nieuwe versie ${(resp as { versie?: number }).versie ?? ''} opgeslagen — nog niet verstuurd` : 'Wijzigingen opgeslagen');
      setNieuweVersieModus(false);
      setEditing(false);
      setDeepEdit(false);
      setRematchQueue([]);
      setPendingItems([]);
      q.refetch();
    },
    onError: (e) => {
      const code = e instanceof ApiError && e.payload && typeof e.payload === 'object' ? (e.payload as { code?: string }).code : undefined;
      if (code === 'reden_prijsafwijking') setToonFouten(true);
      if (code === 'verstuurd') {
        if (confirm(`${foutTekst(e)}\n\nNieuwe versie maken?`)) {
          setNieuweVersieModus(true);
          toast.success('Klik opnieuw op Opslaan om de nieuwe versie te bewaren.');
        }
        return;
      }
      toast.error('Opslaan mislukt: ' + foutTekst(e));
    },
  });

  // Vlag "gefactureerd door de onderaannemer" — manueel (SPEC_workflow 5 en 10).
  const oaGefactureerdMutation = useMutation({
    mutationFn: (aan: boolean) => api.projects.zetOaGefactureerd(id!, aan),
    onSuccess: () => q.refetch(),
    onError: (e) => toast.error('Bijwerken mislukt: ' + foutTekst(e)),
  });

  // Vrijgeven van open posten (27okt 4): nieuwe versie + aanvulling.
  const vrijgaveMutation = useMutation({
    mutationFn: (postIds: string[]) => api.projects.vrijgeven(id!, postIds),
    onSuccess: (r) => { toast.success(`Vrijgegeven: ${fmtEUR(r.bedrag)} — versie ${r.versie}. De aanvulling kan naar de onderaannemer.`); q.refetch(); },
    onError: (e) => toast.error('Vrijgeven mislukt: ' + foutTekst(e)),
  });
  const aanvullingMutation = useMutation({
    mutationFn: (vrijgaveId: string) => api.projects.mailAanvulling(id!, vrijgaveId),
    onSuccess: (r) => { toast.success(`Aanvulling verzonden naar ${r.sentTo}`); q.refetch(); },
    onError: (e) => toast.error('Verzenden mislukt: ' + foutTekst(e)),
  });

  const approveMutation = useMutation({
    mutationFn: () => api.projects.update(id!, { status: 'approved' }),
    onSuccess: () => { toast.success('Project goedgekeurd'); q.refetch(); },
    onError: (e) => toast.error('Goedkeuren mislukt: ' + foutTekst(e)),
  });

  /** Controleert de extra regels vóór het opslaan; toont de fouten in de blokken. */
  const regelsInOrde = () => {
    const fout = extraRegels.some((r) => valideerExtraRegel(r).length > 0);
    if (fout) {
      setToonFouten(true);
      toast.error('Vul de extra werken en aanrekenbare kosten volledig in (omschrijving, bedrag, telt mee, reden).');
      return false;
    }
    // Een afwijkende prijs vraagt een reden van minstens 5 tekens (27okt 2).
    const zonderReden = afwijkingenZonderReden(afwijkingen);
    if (zonderReden.length) {
      setToonFouten(true);
      toast.error(`Geef een reden voor de afwijkende prijs van ${zonderReden.map((a) => a.post).join(', ')} (blok Prijsafwijkingen).`);
      return false;
    }
    return true;
  };

  /** Bewerken starten; een verstuurde calculatie wordt een nieuwe versie. */
  const startBewerken = () => {
    const d = q.data?.project;
    if (d?.verstuurd) {
      if (!confirm(`Versie ${d.versie} werd naar de onderaannemer verstuurd en blijft bewaard. Een aanpassing wordt versie ${d.versie + 1}, die je opnieuw moet versturen. Doorgaan?`)) return;
      setNieuweVersieModus(true);
    }
    setEditing(true);
  };

  /** Bevestigde gevelkeuze uit de pop-up. Buiten de bewerkmodus meteen opslaan. */
  const bevestigGevel = async (cfg: GevelConfig) => {
    setGevel(cfg);
    setGevelModal(null);
    if (!editing) {
      try {
        await api.projects.update(id!, { gevel: { variant: cfg.variant, pricelistId: cfg.pricelistId, bevestigd: true } });
        toast.success(`Prijslijst ${cfg.variant} bevestigd`);
        q.refetch();
      } catch (e) {
        toast.error('Bevestigen mislukt: ' + foutTekst(e));
        q.refetch();
      }
    }
  };

  /** Slaat eerst op (indien in edit-mode) en keurt dan goed — werkt vanuit elke staat. */
  const saveAndApprove = async () => {
    try {
      if (editing) {
        if (!regelsInOrde()) return;
        await saveMutation.mutateAsync();
      }
      await approveMutation.mutateAsync();
      setEditing(false);
    } catch {
      // Toasts uit de mutaties geven al feedback.
    }
  };

  // === Rematch: verplaats een sub-item uit een matched line terug naar de rematch-queue ===
  const startRematch = (lineId: string, sub: { parsedItemId: string; pdfNaam: string; hoeveelheid: number; eenheid: string }) => {
    setRematchQueue((prev) => [...prev, { ...sub, originLineId: lineId }]);
    // Verwijder sub-item uit de matched line
    setMatchedLines((prev) => prev.flatMap((l) => {
      if (l.id !== lineId) return [l];
      const newSubs = l.subItems.filter((s) => s.parsedItemId !== sub.parsedItemId);
      if (!newSubs.length) return [];
      const allEqual = newSubs.every((s) => s.prijs === newSubs[0]!.prijs);
      return [{
        ...l,
        subItems: newSubs,
        hoeveelheid: newSubs.reduce((s, x) => s + x.hoeveelheid, 0),
        subtotaal: newSubs.reduce((s, x) => s + x.subtotaal, 0),
        prijs: allEqual ? newSubs[0]!.prijs : null,
        consumedParsedItemIds: l.consumedParsedItemIds.filter((id) => id !== sub.parsedItemId),
      }];
    }));
  };

  const resolveRematch = (parsedItemId: string, plItem: PricelistItem) => {
    const item = rematchQueue.find((r) => r.parsedItemId === parsedItemId);
    if (!item) return;
    const sub: MatchedSubItem = {
      parsedItemId: item.parsedItemId,
      pdfNaam: item.pdfNaam,
      plItemId: plItem.id,
      hoeveelheid: item.hoeveelheid,
      eenheid: plItem.eenheid,
      prijs: plItem.prijs,
      subtotaal: item.hoeveelheid * plItem.prijs,
      confidence: 1.0,
      autoMatched: false,
      matchSource: 'manual',
    };
    setMatchedLines((prev) => {
      const existing = prev.find((l) => l.naamArbeid === plItem.naamArbeid);
      if (existing) {
        const newSubs = [...existing.subItems, sub];
        const totHv = newSubs.reduce((s, x) => s + x.hoeveelheid, 0);
        const totSt = newSubs.reduce((s, x) => s + x.subtotaal, 0);
        const allSame = newSubs.every((x) => x.prijs === existing.prijs);
        return prev.map((l) => l.id === existing.id ? {
          ...l, hoeveelheid: totHv, subtotaal: totSt,
          prijs: allSame ? existing.prijs : null,
          consumedParsedItemIds: [...l.consumedParsedItemIds, item.parsedItemId],
          subItems: newSubs,
        } : l);
      }
      return [...prev, {
        id: uidLocal('ml'),
        naamArbeid: plItem.naamArbeid,
        eenheid: plItem.eenheid,
        prijs: plItem.prijs,
        hoeveelheid: sub.hoeveelheid,
        subtotaal: sub.subtotaal,
        consumedParsedItemIds: [item.parsedItemId],
        autoMatched: false,
        subItems: [sub],
      }];
    });
    setRematchQueue((prev) => prev.filter((r) => r.parsedItemId !== parsedItemId));
  };

  const cancelRematch = (parsedItemId: string) => {
    setRematchQueue((prev) => prev.filter((r) => r.parsedItemId !== parsedItemId));
  };

  // === Deep edit: opnieuw matchen ===
  const relevantPlItems = useMemo(
    () => (pricelistQ.data?.items ?? []).filter((it) => !it.isNvt && it.naamArbeid !== 'NVT'),
    [pricelistQ.data?.items],
  );

  const handleRematch = () => {
    const pl = pricelistQ.data;
    if (!pl || !editedParsedItems.length) return;
    const result = runMatching(editedParsedItems, pl, learningToMatchingOptions(learningQ.data, pl));
    setMatchedLines(result.matchedLines);
    setMeerwerkLines(result.meerwerkLines);
    setSkippedItems(result.skippedItems);
    setPendingItems([
      ...result.reviewItems.map((r) => ({ parsedItem: r.parsedItem, suggestions: r.suggestions, kind: 'review' as const })),
      ...result.unmatchedItems.map((u) => ({ parsedItem: u.parsedItem, suggestions: u.suggestions, kind: 'unmatched' as const })),
    ]);
    setRematchQueue([]);
    toast.success(`Hermatcht: ${result.matchedLines.length} gematcht, ${result.reviewItems.length + result.unmatchedItems.length} te reviewen`);
  };

  const resolvePendingToMatch = (parsedItemId: string, plItemId: string) => {
    const pi = pendingItems.find((p) => p.parsedItem.id === parsedItemId);
    const plItem = relevantPlItems.find((it) => it.id === plItemId);
    if (!pi || !plItem) return;
    const sub: MatchedSubItem = {
      parsedItemId: pi.parsedItem.id,
      pdfNaam: pi.parsedItem.werken,
      plItemId: plItem.id,
      hoeveelheid: pi.parsedItem.werkelijkeHoeveelheid || 0,
      eenheid: plItem.eenheid,
      prijs: plItem.prijs,
      subtotaal: (pi.parsedItem.werkelijkeHoeveelheid || 0) * plItem.prijs,
      confidence: 1.0,
      autoMatched: false,
      matchSource: 'manual',
    };
    setMatchedLines((prev) => {
      const existing = prev.find((l) => l.naamArbeid === plItem.naamArbeid);
      if (existing) {
        const newSubs = [...existing.subItems, sub];
        const totHv = newSubs.reduce((s, x) => s + x.hoeveelheid, 0);
        const totSt = newSubs.reduce((s, x) => s + x.subtotaal, 0);
        const allSame = newSubs.every((x) => x.prijs === existing.prijs);
        return prev.map((l) => l.id === existing.id ? {
          ...l, hoeveelheid: totHv, subtotaal: totSt,
          prijs: allSame ? existing.prijs : null,
          consumedParsedItemIds: [...l.consumedParsedItemIds, pi.parsedItem.id],
          subItems: newSubs,
        } : l);
      }
      return [...prev, {
        id: uidLocal('ml'),
        naamArbeid: plItem.naamArbeid,
        eenheid: plItem.eenheid,
        prijs: plItem.prijs,
        hoeveelheid: sub.hoeveelheid,
        subtotaal: sub.subtotaal,
        consumedParsedItemIds: [pi.parsedItem.id],
        autoMatched: false,
        subItems: [sub],
      }];
    });
    setPendingItems((prev) => prev.filter((p) => p.parsedItem.id !== parsedItemId));
  };

  const resolvePendingToMeerwerk = (parsedItemId: string, prijs: number) => {
    const pi = pendingItems.find((p) => p.parsedItem.id === parsedItemId);
    if (!pi) return;
    const hv = pi.parsedItem.werkelijkeHoeveelheid || 0;
    setMeerwerkLines((prev) => [...prev, {
      id: uidLocal('mw'),
      pdfNaam: pi.parsedItem.werken,
      naamArbeid: 'MEERWERK',
      categorie: pi.parsedItem.categorie,
      eenheid: pi.parsedItem.eenheid || '',
      hoeveelheid: hv,
      prijs,
      subtotaal: prijs * hv,
      parsedItemId: pi.parsedItem.id,
    }]);
    setPendingItems((prev) => prev.filter((p) => p.parsedItem.id !== parsedItemId));
  };

  const skipPending = (parsedItemId: string) => {
    const pi = pendingItems.find((p) => p.parsedItem.id === parsedItemId);
    if (pi) {
      setSkippedItems((prev) => [
        ...prev.filter((s) => s.parsedItemId !== parsedItemId),
        { parsedItemId, pdfNaam: pi.parsedItem.werken, reden: USER_SKIP_REDEN },
      ]);
    }
    setPendingItems((prev) => prev.filter((p) => p.parsedItem.id !== parsedItemId));
  };

  // 8.6: na het versturen meldt de server of de PDF ook in Bouwflow staat (enkel als de upload aan staat).
  const meldBouwflowUpload = (b?: BouwflowUploadAntwoord) => {
    if (!b || b.status === 'uit' || b.status === 'al_geplaatst') return;
    if (b.status === 'gelukt') toast.success('Calculatie ook in de projectbestanden van Bouwflow geplaatst');
    else toast.warning(`Niet in Bouwflow geplaatst: ${b.fout ?? 'onbekende fout'}`);
  };

  const emailMutation = useMutation({
    mutationFn: (body: { customMessage?: string; ccCurrentUser?: boolean; ccBoekhouding?: boolean }) => api.projects.email(id!, body),
    onSuccess: (resp) => {
      toast.success(`Email verzonden naar ${resp.sentTo}`);
      meldBouwflowUpload(resp.bouwflow);
      setShowEmail(false);
      setEmailMessage('');
      q.refetch();
    },
    onError: (e) => {
      const msg = e instanceof ApiError && e.payload && typeof e.payload === 'object' && 'error' in e.payload
        ? String((e.payload as { error: string }).error)
        : 'Email verzenden mislukt';
      toast.error(msg);
    },
  });

  const whatsappMutation = useMutation({
    mutationFn: () => api.projects.whatsapp(id!),
    onSuccess: (resp) => {
      toast.success(`WhatsApp verzonden naar +${resp.sentTo}`);
      meldBouwflowUpload(resp.bouwflow);
      q.refetch();
    },
    onError: (e) => {
      const msg = e instanceof ApiError && e.payload && typeof e.payload === 'object' && 'error' in e.payload
        ? String((e.payload as { error: string }).error)
        : 'WhatsApp verzenden mislukt';
      toast.error(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.projects.remove(id!),
    onSuccess: () => { toast.success('Verwijderd'); navigate('/'); },
    onError: () => toast.error('Verwijderen mislukt'),
  });

  if (q.isLoading) return <AppShell><p>Laden…</p></AppShell>;
  if (q.error || !q.data) return <AppShell><div className="text-center py-16"><h2 className="text-2xl font-bold mb-4">Project niet gevonden</h2><Link to="/" className="btn-ghost">Terug</Link></div></AppShell>;

  const p = q.data.project;
  const pricelist = pricelistQ.data ?? null;
  // Referenties uit Bouwflow (27okt 3): aanvaarde offertes en verstuurde facturen min creditnota's.
  const referentie = p.projectPercentage?.referentie ?? null;
  const gefactureerdRef = referentie ? referentie.gefactureerd : p.gefactureerd ?? null;

  return (
    <AppShell>
      <PdfViewerPanel
        url={api.projects.sourcePdfUrl(p.id)}
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        title="Opstartfiche"
      />
      {gevelModal && (
        <GevelVariantModal
          ploegId={ploegId || p.ploegId}
          standaardVariant={p.gevelStandaard}
          huidigeVariant={gevel?.variant}
          magBevestigen={magBevestigen}
          verversen={gevelModal === 'verversen'}
          onBevestig={bevestigGevel}
          onSluit={() => setGevelModal(null)}
        />
      )}
      <header className="mb-5 normal-case tracking-normal">
        <Link to="/" className="text-secondary text-sm">← Terug</Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          {/* basis-0 + flex-1: de kenmerken lopen binnen deze kolom door, de knoppen blijven rechts staan. */}
          <div className="min-w-[min(100%,320px)] flex-1 basis-0">
            <h1 className="page-title">{p.klantNaam || 'Project'}</h1>
            <p className="text-secondary text-sm mt-1">
              {p.projectNummer ? (
                <BouwflowLink bouwflowProjectId={p.bouwflowProjectId} className="font-bold text-primary mr-3 hover:text-accent-700">{p.projectNummer}</BouwflowLink>
              ) : null}
              {p.klantAdres} · Adviseur: {p.adviseur || '—'}{p.projectleider ? ` · Projectleider: ${p.projectleider}` : ''} · {fmtDate(p.createdAt)}
            </p>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <span className={statusChipClass(p.status)}>{statusLabel(p.status)}</span>
              {p.verstuurd
                ? <span className="chip-neutral" title={`Versie ${p.versie} verstuurd op ${fmtDate(p.verstuurdOp)}`}>verstuurd · v{p.versie}</span>
                : <span className="chip-neutral" title={p.verstuurdeVersie ? `Versie ${p.verstuurdeVersie} werd verstuurd; deze versie nog niet` : 'Nog niet verstuurd'}>v{p.versie}{p.verstuurdeVersie ? ' · niet verstuurd' : ''}</span>}
              <span className="chip">{p.ploegTeam} · {p.ploegFirma}</span>
              {gevel && isGevel && <span className={gevel.bevestigd ? 'chip-neutral' : 'chip-warning'} title="Gebruikte gevelprijslijst">Gevel {gevel.variant}{gevel.bevestigd ? '' : ' (te bevestigen)'}</span>}
              {p.workflow && <WorkflowKenmerken w={p.workflow} verstuurd={!!p.verstuurdOp} />}
              {p.bouwflowUploadOp && (
                p.bouwflowUploadFout
                  ? <span className="chip-danger" title={`Laatste poging ${fmtDateTime(p.bouwflowUploadOp)}: ${p.bouwflowUploadFout}`}>Niet in Bouwflow geplaatst</span>
                  : <span className="chip-neutral" title={`Calculatie-PDF in de projectbestanden van Bouwflow geplaatst op ${fmtDateTime(p.bouwflowUploadOp)}`}>In Bouwflow · v{p.bouwflowUploadVersie}</span>
              )}
              <label className="inline-flex items-center gap-1.5 text-xs" title={p.oaGefactureerdOp ? `Aangevinkt door ${p.oaGefactureerdDoor ?? '—'} op ${fmtDate(p.oaGefactureerdOp)}` : 'Heeft de onderaannemer al gefactureerd voor deze calculatie?'}>
                <input type="checkbox" checked={!!p.oaGefactureerdOp} disabled={oaGefactureerdMutation.isPending} onChange={(e) => oaGefactureerdMutation.mutate(e.target.checked)} />
                gefactureerd door OA
              </label>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!editing && (
              <button className="btn-ghost btn-sm" onClick={startBewerken}>Bewerken</button>
            )}
            {editing && (
              <>
                {!deepEdit && (
                  <button className="btn-ghost btn-sm" onClick={() => setDeepEdit(true)} title="Parsed items bewerken en opnieuw matchen">↻ Hermatchen</button>
                )}
                <button className="btn-ghost btn-sm" onClick={() => { setEditing(false); setNieuweVersieModus(false); setDeepEdit(false); setRematchQueue([]); setPendingItems([]); q.refetch(); }}>Annuleren</button>
                <button className="btn-primary btn-sm" onClick={() => { if (regelsInOrde()) saveMutation.mutate(); }} disabled={saveMutation.isPending || pendingItems.length > 0}>
                  {saveMutation.isPending ? 'Opslaan…' : 'Opslaan'}
                </button>
              </>
            )}
            {p.status !== 'approved' && (
              <button
                className="btn-primary btn-sm"
                onClick={saveAndApprove}
                disabled={approveMutation.isPending || saveMutation.isPending || !!blokkeerGoedkeuren}
                title={blokkeerGoedkeuren ?? (editing ? 'Slaat eerst de wijzigingen op en keurt dan goed' : undefined)}
              >
                {editing ? 'Opslaan + goedkeuren' : 'Goedkeuren'}
              </button>
            )}
            {!editing && (
              <button
                className="btn-danger btn-sm"
                onClick={() => { if (confirm(`Project "${p.klantNaam}" verwijderen?`)) deleteMutation.mutate(); }}
              >
                Verwijderen
              </button>
            )}
          </div>
        </div>

        {/* Acties, gegroepeerd: documenten · versturen · dossier. Bewerken en goedkeuren staan bij de titel. */}
        <div className="mt-4 flex flex-wrap items-center gap-2" role="toolbar" aria-label="Acties">
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setPdfViewerOpen((v) => !v)}
            title="Open de opstartfiche naast de calculatie voor verificatie"
          >{pdfViewerOpen ? <><Icon name="sluiten" size={16} />Sluit fiche</> : 'Opstartfiche'}</button>
          <a className="btn-ghost btn-sm" href={api.projects.exportPdfUrl(p.id)} download><Icon name="download" size={16} />PDF</a>
          <a className="btn-ghost btn-sm" href={api.projects.exportXlsxUrl(p.id)} download><Icon name="download" size={16} />Excel</a>
          <BouwflowLink bouwflowProjectId={p.bouwflowProjectId} className="btn-ghost btn-sm">Bouwflow</BouwflowLink>
          <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden />
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={emailMutation.isPending}
            title="Stuur de PDF + tekst via email naar de onderaannemer"
            onClick={() => setShowEmail(true)}
          >
            {emailMutation.isPending ? 'Verzenden…' : <><Icon name="mail" size={16} />Email</>}
          </button>
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={whatsappMutation.isPending}
            title="Stuur de PDF + bericht via WhatsApp Business naar de onderaannemer"
            onClick={() => {
              if (confirm(`WhatsApp-bericht versturen voor project ${(p as { projectNummer?: string }).projectNummer || ''} (${p.klantNaam}) naar ${p.ploegTeam}?\n\nBedrag: ${fmtEUR(totals.totaal)}`)) {
                whatsappMutation.mutate();
              }
            }}
          >
            {whatsappMutation.isPending ? 'Verzenden…' : <><Icon name="chat" size={16} />WhatsApp</>}
          </button>
          <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden />
          <button type="button" className="btn-ghost btn-sm" onClick={() => setDossierFocus((f) => ({ soort: 'notitie', n: (f?.n ?? 0) + 1 }))} title="Notitie schrijven bij dit project">
            Notitie
          </button>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setDossierFocus((f) => ({ soort: 'tag', n: (f?.n ?? 0) + 1 }))} title="Tag een collega in een notitie: die krijgt een mail">
            @ Collega taggen
          </button>
          <button type="button" className="btn-ghost btn-sm" onClick={() => { setLabelSignaal((n) => n + 1); document.getElementById('dossier')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} title="Label toevoegen (bv. wacht op stelling, prioriteit)">
            <Icon name="tags" size={16} />Label
          </button>
        </div>
      </header>

      <WorkflowMeldingen meldingen={p.meldingen} calcId={p.id} />

      <DossierPaneel sleutel={p.projectSleutel} calcId={p.id} tags={p.tags} focus={dossierFocus} labelSignaal={labelSignaal} />

      {editing && nieuweVersieModus && (
        <div className="card mb-5 border-l-4 border-accent normal-case tracking-normal" role="status">
          <strong>Nieuwe versie {p.versie + 1}.</strong> Versie {p.versie} werd verstuurd en blijft bewaard zoals de onderaannemer ze kreeg. Na opslaan verstuur je de nieuwe versie opnieuw.
        </div>
      )}

      {/* Edit-form voor metadata (alleen in edit-mode) */}
      {editing && (
        <div className="card mb-5">
          <h3 className="text-lg font-bold mb-3">Projectgegevens</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Projectnummer</label>
              <input className="input" placeholder="bv. P-1842" value={meta.projectNummer} onChange={(e) => setMeta({ ...meta, projectNummer: normalizeProjectNummer(e.target.value) })} />
            </div>
            <div>
              <label className="field-label">Klant</label>
              <input className="input" value={meta.klantNaam} onChange={(e) => setMeta({ ...meta, klantNaam: e.target.value })} />
            </div>
            <div>
              <label className="field-label">Adres</label>
              <input className="input" value={meta.klantAdres} onChange={(e) => setMeta({ ...meta, klantAdres: e.target.value })} />
            </div>
            <div>
              <label className="field-label">Adviseur</label>
              <input className="input" value={meta.adviseur} onChange={(e) => setMeta({ ...meta, adviseur: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="field-label">Onderaannemer</label>
              <select
                className="select w-full"
                value={ploegId}
                onChange={(e) => setPloegId(e.target.value)}
                title="Corrigeer de toegewezen onderaannemer als de automatische match fout zat"
              >
                {allPloegen.map((pl) => (
                  <option key={pl.id} value={pl.id}>{pl.team} — {pl.firma}</option>
                ))}
              </select>
              {ploegChanged && (
                <p className="text-xs text-warning mt-1 normal-case tracking-normal">
                  ⚠ Onderaannemer wordt gewijzigd. De prijslijst volgt de nieuwe ploeg — controleer na opslaan de werkposten en gebruik desnoods “↻ Hermatchen”.
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="field-label">Projecttitel</label>
              <input className="input" value={meta.projectTitel} onChange={(e) => setMeta({ ...meta, projectTitel: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="field-label">Opmerkingen / instructies uit de opstartfiche</label>
              <textarea
                className="textarea font-normal normal-case tracking-normal"
                rows={Math.max(3, meta.opmerkingen.split('\n').length + 1)}
                value={meta.opmerkingen}
                placeholder="Speciale voorwaarden, prijsregelingen, afspraken…"
                onChange={(e) => setMeta({ ...meta, opmerkingen: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {/* Read-only opmerkingen-blok als er iets staat én niet in edit-mode */}
      {!editing && meta.opmerkingen.trim() && (
        <div className="card mb-5 border-l-4 border-warning">
          <h3 className="text-base font-bold flex items-center gap-2 mb-2">
            <span className="text-warning">⚠</span> Opmerkingen / instructies
          </h3>
          <p className="whitespace-pre-wrap font-normal normal-case tracking-normal text-sm">{meta.opmerkingen}</p>
        </div>
      )}

      {/* Deep edit: geparseerde items + opnieuw matchen + pending items */}
      {editing && deepEdit && (
        <>
          <div className="card mb-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-lg font-bold">Geparseerde items ({editedParsedItems.length})</h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-secondary">Pas beschrijvingen of hoeveelheden aan en klik daarna "Opnieuw matchen"</span>
                <button className="btn-ghost btn-sm" onClick={() => {
                  const id = 'pi_manual_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
                  setEditedParsedItems((prev) => [...prev, { id, categorie: 'HANDMATIG TOEGEVOEGD', werken: '', geadviseerdeHoeveelheid: 0, werkelijkeHoeveelheid: 0, eenheid: 'STUK' }]);
                }}>+ Item toevoegen</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="thead-row">
                    <th className="text-left px-3 py-2">Beschrijving</th>
                    <th className="text-right px-3 py-2 w-28">Geadviseerd</th>
                    <th className="text-right px-3 py-2 w-28">Werkelijk</th>
                    <th className="text-left px-3 py-2 w-20">Eenheid</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {editedParsedItems.map((it) => (
                    <tr key={it.id} className="border-b border-lightgrey">
                      <td className="px-3 py-1">
                        <input className="w-full bg-transparent text-sm py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm" value={it.werken} onChange={(e) => setEditedParsedItems((prev) => prev.map((p) => p.id === it.id ? { ...p, werken: e.target.value } : p))} />
                      </td>
                      <td className="px-3 py-1 text-right">
                        <input type="number" step="0.01" className="w-full text-right bg-transparent text-sm py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm" value={it.geadviseerdeHoeveelheid} onChange={(e) => setEditedParsedItems((prev) => prev.map((p) => p.id === it.id ? { ...p, geadviseerdeHoeveelheid: parseFloat(e.target.value) || 0 } : p))} />
                      </td>
                      <td className="px-3 py-1 text-right">
                        <input type="number" step="0.01" className="w-full text-right bg-transparent text-sm py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm" value={it.werkelijkeHoeveelheid} onChange={(e) => setEditedParsedItems((prev) => prev.map((p) => p.id === it.id ? { ...p, werkelijkeHoeveelheid: parseFloat(e.target.value) || 0 } : p))} />
                      </td>
                      <td className="px-3 py-1">
                        <input className="w-full bg-transparent text-sm py-1 uppercase hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm" value={it.eenheid} onChange={(e) => setEditedParsedItems((prev) => prev.map((p) => p.id === it.id ? { ...p, eenheid: e.target.value.toUpperCase() } : p))} />
                      </td>
                      <td className="px-3 py-1">
                        <button className="btn-ghost btn-sm" onClick={() => setEditedParsedItems((prev) => prev.filter((p) => p.id !== it.id))}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end mt-4 pt-3 border-t border-border">
              <button className="btn-accent" onClick={handleRematch} disabled={!pricelistQ.data || editedParsedItems.length === 0}>
                ↻ Opnieuw matchen
              </button>
            </div>
          </div>

          {/* Pending items na hermatchen */}
          {pendingItems.length > 0 && (
            <div className="card mb-5 border-l-4 border-warning">
              <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                <span className="text-warning">!</span> Te reviewen ({pendingItems.length})
              </h3>
              <p className="text-sm text-secondary mb-3">Items die niet automatisch gematcht konden worden — kies een werkpost, maak er meerwerk van, of negeer.</p>
              <div className="space-y-2">
                {pendingItems.map((pi) => (
                  <PendingRow
                    key={pi.parsedItem.id}
                    pending={pi}
                    pricelistItems={relevantPlItems}
                    onMatch={(plItemId) => resolvePendingToMatch(pi.parsedItem.id, plItemId)}
                    onMeerwerk={(prijs) => resolvePendingToMeerwerk(pi.parsedItem.id, prijs)}
                    onSkip={() => skipPending(pi.parsedItem.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Summary */}
      <div className="bg-white rounded-lg shadow-card mb-5 overflow-hidden">
        <div className="bg-primary text-white px-5 py-3 flex justify-between text-sm font-semibold">
          <span>Netto te betalen aan onderaannemer</span>
          <span>{p.pricelistName}</span>
        </div>
        <div className="p-6">
          <div className="text-4xl sm:text-5xl font-extrabold text-primary tracking-normal normal-case tabular-nums whitespace-nowrap">{fmtEUR(totals.totaal)}</div>
          {/* Bovenste rij (lg): opbouw van het netto bedrag — onderste rij: t.o.v. de projectwaarde.
              Kolommen blijven breed genoeg (max 4) zodat bedragen nooit afbreken of overlappen. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-5 mt-5 pt-5 border-t border-border">
            <SummaryStat label="Subtotaal" value={fmtEUR(totals.subtotaal)} />
            <SummaryStat label="Meerwerk" value={fmtEUR(totals.meerwerk)} />
            <SummaryStat label="Extra werken" value={fmtEUR(totals.extraWerk)} />
            <SummaryStat label="Aanrekenbare kosten" value={totals.aanrekening > 0 ? '−' + fmtEUR(totals.aanrekening) : fmtEUR(0)} danger />

            <SummaryStat label="Offertewaarde">
              {referentie?.offertewaarde != null ? (
                // Uit Bouwflow: som van de aanvaarde offertes (offerte + meerwerkoffertes), excl. btw.
                <div
                  className="text-base sm:text-lg font-bold normal-case tracking-normal tabular-nums whitespace-nowrap"
                  title={`Aanvaarde offertes in Bouwflow: ${referentie.offertes.filter((o) => ['approved', 'partly_invoiced', 'fully_invoiced'].includes(o.status)).map((o) => `${o.nummer ?? o.id} ${fmtEUR(o.bedrag)}`).join(', ')}`}
                >{fmtEUR(referentie.offertewaarde)}</div>
              ) : p.bouwflowProjectId != null ? (
                // Gekoppeld aan Bouwflow: enkel de aanvaarde offertes tellen, nooit het oude `budget`.
                <div className="text-base sm:text-lg font-bold normal-case tracking-normal text-secondary" title="Wordt opgehaald uit de aanvaarde offertes in Bouwflow">—</div>
              ) : editing ? (
                <input
                  type="number"
                  step="0.01"
                  className="border border-border rounded px-2 py-1 w-full max-w-[10rem] text-right tabular-nums text-lg font-bold"
                  value={offertewaarde ?? ''}
                  placeholder="—"
                  onChange={(e) => setOffertewaarde(e.target.value ? parseFloat(e.target.value) : null)}
                />
              ) : (
                <div className="text-base sm:text-lg font-bold normal-case tracking-normal tabular-nums whitespace-nowrap">{offertewaarde != null ? fmtEUR(offertewaarde) : '—'}</div>
              )}
            </SummaryStat>

            <SummaryStat
              label="Gefactureerd"
              value={gefactureerdRef != null ? fmtEUR(gefactureerdRef) : 'nog niet gefactureerd'}
              title="Verstuurde verkoopfacturen (voorschotten inbegrepen) min creditnota's, excl. btw, uit Bouwflow"
            />

            {p.openVoorOplevering > 0 && (
              <SummaryStat label="Nu factureerbaar" value={fmtEUR(p.nuFactureerbaar)} title={`${fmtEUR(p.openVoorOplevering)} staat open voor oplevering`} />
            )}

            {editing && (
              <SummaryStat
                label="% Project (live)"
                title={live.eigen.noemer != null
                  ? `Kost van het hele project ${live.eigen.label} t.o.v. ${live.eigen.basis} (${fmtEUR(live.eigen.noemer)})${live.eigen.voorlopig ? ' — voorlopig' : ''}`
                  : 'Geen referentie (offertewaarde of facturen) bekend'}
              >
                <div className="text-base sm:text-lg font-bold normal-case tracking-normal whitespace-nowrap">
                  <PctWaarde pct={live.eigen.naar} voorlopig={live.eigen.voorlopig} />
                </div>
              </SummaryStat>
            )}
          </div>
          {/* Twee percentages op projectniveau (27okt 3) — intern, niet op de PDF. */}
          {p.projectPercentage && (
            <div className="mt-5 pt-4 border-t border-border">
              <div className="text-xs font-medium text-secondary mb-1">Percentage onderaanneming (project)</div>
              <TweePercentages data={p.projectPercentage.percentages} detail />
            </div>
          )}
        </div>
      </div>

      {/* Rematch queue — items die opnieuw gematcht moeten worden */}
      {editing && rematchQueue.length > 0 && pricelist && (
        <div className="card mb-5 border-l-4 border-warning">
          <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
            <span className="text-warning">↻</span> Opnieuw matchen ({rematchQueue.length})
          </h3>
          <p className="text-sm text-secondary mb-3">Kies een nieuwe werkpost uit de prijslijst voor elk item.</p>
          <div className="space-y-2">
            {rematchQueue.map((item) => (
              <RematchRow
                key={item.parsedItemId}
                item={item}
                pricelistItems={(pricelist.items ?? []).filter((it) => !it.isNvt && it.naamArbeid !== 'NVT')}
                onResolve={(plItem) => resolveRematch(item.parsedItemId, plItem)}
                onCancel={() => cancelRematch(item.parsedItemId)}
              />
            ))}
          </div>
        </div>
      )}

      {p.projectPercentage && <ProjectNiveauKaart project={p.projectPercentage} huidigeCalcId={p.id} />}

      <OverlapVragen lines={matchedLines} editable={editing} magBevestigen={magBevestigen} onBeslis={(lineId, status) => setMatchedLines((prev) => beslisOverlap(prev, lineId, status))} />

      {/* Werkposten */}
      <SectionCard title="Werkposten" subtotal={totals.subtotaal}>
        <MatchedLinesTable
          lines={matchedLines}
          editing={editing}
          onUpdate={setMatchedLines}
          onRematch={startRematch}
          vervangenIds={vervangenIds}
          afwijkendeIds={new Set(afwijkingen.filter((a) => a.soort === 'werkpost').map((a) => a.lijnId))}
        />
        {editing && pricelist && (
          <AddPricelistItemRow
            pricelist={pricelist}
            existing={matchedLines}
            onAdd={(line) => setMatchedLines((prev) => [...prev, line])}
          />
        )}
      </SectionCard>

      <GevelBlock
        gevel={gevel}
        resultaat={totals.gevel}
        isGevel={isGevel}
        standaardVariant={p.gevelStandaard}
        editable={editing || p.status !== 'approved'}
        onKies={() => setGevelModal('kies')}
        onVerversen={() => setGevelModal('verversen')}
      />

      {/* Meerwerk — altijd zichtbaar in edit-mode zodat je kan toevoegen */}
      {(meerwerkLines.length > 0 || editing) && (
        <SectionCard title="Meerwerk" subtotal={totals.meerwerk} accent="accent">
          <MeerwerkLinesTable
            lines={meerwerkLines}
            editing={editing}
            onUpdate={setMeerwerkLines}
            vervangenIds={new Set(totals.gevel?.vervangenMeerwerkIds ?? [])}
          />
          {editing && pricelist && (
            <AddMeerwerkRow
              pricelist={pricelist}
              onAdd={(line) => setMeerwerkLines((prev) => [...prev, line])}
            />
          )}
        </SectionCard>
      )}

      {/* Overgeslagen items */}
      {skippedItems.length > 0 && (
        <div className="card mb-5">
          <h3 className="text-lg font-bold mb-3 text-secondary">Overgeslagen items</h3>
          {skippedItems.map((s, i) => (
            <div key={i} className="flex justify-between text-sm py-1 border-b border-lightgrey last:border-b-0 normal-case tracking-normal">
              <span>{s.pdfNaam}</span>
              <span className="text-secondary">{s.reden}</span>
            </div>
          ))}
        </div>
      )}

      {/* Prijsafwijkingen t.o.v. standaardprijzen (27okt 2) — direct onder de overgeslagen items, intern. */}
      <PrijsafwijkingenBlok
        afwijkingen={afwijkingen}
        calculatieTotaal={totals.totaal}
        editing={editing}
        toonFouten={toonFouten}
        onReden={(a, reden) => {
          if (a.soort === 'werkpost') setMatchedLines((prev) => zetReden(prev, a.lijnId, reden));
          else setMeerwerkLines((prev) => zetReden(prev, a.lijnId, reden));
        }}
      />

      {/* Extra werken (nieuw) en aanrekenbare kosten — SPEC_wijzigingen 3 en 4 */}
      <ExtraRegelsSection
        soort="extra_werk"
        regels={extraRegels}
        onChange={setExtraRegels}
        editing={editing}
        projectNummer={meta.projectNummer}
        ploegId={ploegId || p.ploegId}
        ploegen={allPloegen}
        magBevestigen={magBevestigen}
        toonFouten={toonFouten}
      />
      <ExtraRegelsSection
        soort="aanrekenbare_kost"
        regels={extraRegels}
        onChange={setExtraRegels}
        editing={editing}
        projectNummer={meta.projectNummer}
        ploegId={ploegId || p.ploegId}
        ploegen={allPloegen}
        magBevestigen={magBevestigen}
        toonFouten={toonFouten}
      />
      <GelinkteProjectenEffect effecten={live.gelinkt} />

      {/* Posten open voor oplevering + vrijgave (27okt 4) */}
      <OpenPostenBlok
        projectId={p.id}
        openPosten={editing ? [] : p.openPosten}
        nuFactureerbaar={editing ? totals.nuFactureerbaar : p.nuFactureerbaar}
        openVoorOplevering={editing ? totals.openVoorOplevering : p.openVoorOplevering}
        vrijgaves={p.vrijgaves}
        magVrijgeven={!!user?.magVrijgeven && !editing}
        bevestigers="Eranda, Timen of Ervin (een rol die mag vrijgeven)"
        bezig={vrijgaveMutation.isPending}
        onVrijgeven={(ids) => vrijgaveMutation.mutate(ids)}
        onMailAanvulling={(vid) => { if (confirm('Aanvulling met de vrijgegeven posten naar de onderaannemer mailen?')) aanvullingMutation.mutate(vid); }}
      />

      <VersiesPaneel projectId={p.id} versies={p.versies} huidigeVersie={p.versie} verstuurdeVersie={p.verstuurdeVersie} />

      {/* Email-modal */}
      {showEmail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={() => !emailMutation.isPending && setShowEmail(false)}>
          <div className="card max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-1">Project doormailen</h2>
            <p className="text-sm text-secondary mb-4">
              Naar <strong>{p.ploegTeam} · {p.ploegFirma}</strong>. PDF wordt automatisch bijgevoegd · {matchedLines.length} werkpost{matchedLines.length === 1 ? '' : 'en'} · totaal <strong>{fmtEUR(totals.totaal)}</strong>.
            </p>
            <label className="field-label">Persoonlijk bericht (optioneel)</label>
            <textarea
              className="input"
              rows={5}
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              placeholder={`In bijlage de afrekening voor project ${(p as { projectNummer?: string }).projectNummer || ''}.`}
              disabled={emailMutation.isPending}
            />
            <label className="flex items-center gap-2 mt-3 text-sm">
              <input type="checkbox" checked={emailCcMe} onChange={(e) => setEmailCcMe(e.target.checked)} disabled={emailMutation.isPending} />
              Mijzelf in BCC zetten
            </label>
            <label className="flex items-center gap-2 mt-2 text-sm">
              <input type="checkbox" checked={emailCcBoekhouding} onChange={(e) => setEmailCcBoekhouding(e.target.checked)} disabled={emailMutation.isPending} />
              Boekhouding (boekhouding@recotex.be) in CC zetten
            </label>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" className="btn-ghost" onClick={() => setShowEmail(false)} disabled={emailMutation.isPending}>Annuleer</button>
              <button
                type="button"
                className="btn-accent"
                disabled={emailMutation.isPending}
                onClick={() => emailMutation.mutate({
                  customMessage: emailMessage.trim() || undefined,
                  ccCurrentUser: emailCcMe,
                  ccBoekhouding: emailCcBoekhouding,
                })}
              >
                {emailMutation.isPending ? 'Verzenden…' : '✉ Verzenden'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

// ===== Sub-components =====

function SummaryStat({ label, value, danger, tone, title, children }: {
  label: string;
  value?: string;
  danger?: boolean;
  tone?: string;
  title?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className={`text-xs font-medium ${danger ? 'text-danger' : 'text-secondary'}`}>{label}</div>
      {children ?? (
        <div
          className={`text-base sm:text-lg font-bold normal-case tracking-normal tabular-nums whitespace-nowrap ${danger ? 'text-danger' : tone ?? ''}`}
          title={title}
        >
          {value}
        </div>
      )}
    </div>
  );
}

function SectionCard({ title, subtotal, accent, children }: {
  title: string;
  subtotal?: number;
  accent?: 'accent' | 'danger';
  children: React.ReactNode;
}) {
  const accentColor = accent === 'accent' ? 'text-accent' : accent === 'danger' ? 'text-danger' : 'text-primary';
  return (
    <div className={`card mb-5${accent === 'danger' ? ' border-l-4 border-danger' : ''}`}>
      <div className="flex justify-between items-baseline mb-3">
        <h3 className={`text-lg font-bold ${accentColor}`}>{title}</h3>
        {typeof subtotal === 'number' && (
          <div className="text-sm text-secondary normal-case tracking-normal">
            Subtotaal: <strong className="text-primary">{fmtEUR(subtotal)}</strong>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function MatchedLinesTable({ lines, editing, onUpdate, onRematch, vervangenIds, afwijkendeIds }: {
  lines: MatchedLine[];
  editing: boolean;
  onUpdate: (lines: MatchedLine[]) => void;
  onRematch?: (lineId: string, sub: { parsedItemId: string; pdfNaam: string; hoeveelheid: number; eenheid: string }) => void;
  /** Lijnen die door de gevelladder geprijsd worden (crepi, EPS, steenstrips). */
  vervangenIds?: Set<string>;
  /** Lijnen met een prijs die afwijkt van de standaardprijs (27okt 2). */
  afwijkendeIds?: Set<string>;
}) {
  const zetOplevering = (lineId: string, o: MatchedLine['oplevering']) =>
    onUpdate(lines.map((l) => (l.id === lineId ? { ...l, oplevering: o } : l)));
  const updateSub = (lineId: string, parsedItemId: string, patch: { hoeveelheid?: number; prijs?: number }) => {
    onUpdate(lines.map((l) => {
      if (l.id !== lineId) return l;
      const newSubItems = l.subItems.map((s) => {
        if (s.parsedItemId !== parsedItemId) return s;
        const merged = { ...s, ...patch };
        merged.subtotaal = merged.hoeveelheid * merged.prijs;
        return merged;
      });
      const allEqual = newSubItems.every((s) => s.prijs === newSubItems[0]!.prijs);
      return {
        ...l,
        subItems: newSubItems,
        hoeveelheid: newSubItems.reduce((s, x) => s + x.hoeveelheid, 0),
        subtotaal: newSubItems.reduce((s, x) => s + x.subtotaal, 0),
        prijs: allEqual ? newSubItems[0]!.prijs : null,
      };
    }));
  };
  const removeLine = (lineId: string) => onUpdate(lines.filter((l) => l.id !== lineId));
  const removeSub = (lineId: string, parsedItemId: string) => {
    onUpdate(lines.flatMap((l) => {
      if (l.id !== lineId) return [l];
      const newSubItems = l.subItems.filter((s) => s.parsedItemId !== parsedItemId);
      if (!newSubItems.length) return [];
      const allEqual = newSubItems.every((s) => s.prijs === newSubItems[0]!.prijs);
      return [{
        ...l,
        subItems: newSubItems,
        hoeveelheid: newSubItems.reduce((s, x) => s + x.hoeveelheid, 0),
        subtotaal: newSubItems.reduce((s, x) => s + x.subtotaal, 0),
        prijs: allEqual ? newSubItems[0]!.prijs : null,
      }];
    }));
  };

  if (!lines.length) return <p className="text-secondary text-sm normal-case tracking-normal">Geen werkposten.</p>;

  return (
    <table className="w-full">
      <thead>
        <tr className="thead-row">
          <th className="text-left px-3 py-2">Naam arbeid</th>
          <th className="text-right px-3 py-2 w-32">Hoeveelheid</th>
          <th className="text-right px-3 py-2 w-28">Prijs</th>
          <th className="text-right px-3 py-2 w-32">Subtotaal</th>
          {editing && <th className="text-left px-3 py-2 w-44">Oplevering</th>}
          {editing && <th className="w-16"></th>}
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => (
          <tr key={line.id} className="border-b border-lightgrey align-top">
            <td className="px-3 py-2 normal-case tracking-normal">
              <strong>{line.naamArbeid}</strong>
              {!editing && <OpleveringChip oplevering={line.oplevering} />}
              {afwijkendeIds?.has(line.id) && <span className="ml-2 inline-flex rounded-full border border-danger/40 px-2 py-0.5 text-[11px] font-bold text-danger" title="Prijs wijkt af van de standaardprijs — zie blok Prijsafwijkingen">afwijkende prijs</span>}
              {line.gevelAccessoire && <span className="chip-neutral ml-2" title="Prijs uit de gevellijst A/B (SPEC_calculatie 7.1)">gevellijst</span>}
              {vervangenIds?.has(line.id) && <span className="chip-neutral ml-2" title="Geprijsd door de crepi-ladder (zie blok Gevel & crepi)">via gevelladder</span>}
              {line.overlap && (
                <span className={`ml-2 ${line.overlap.status === 'bevestigd' ? 'chip-success' : line.overlap.status === 'open' ? 'chip-warning' : 'chip-neutral'}`} title={`Normaal inbegrepen in ${line.overlap.inbegrepenInNaam}`}>
                  {line.overlap.status === 'open' ? 'te bevestigen' : line.overlap.status === 'bevestigd' ? 'apart betaald' : 'niet betaald'}
                </span>
              )}
              {line.schijven && (
                <div className="text-xs text-secondary mt-0.5 tabular-nums">
                  Schijven: {berekenSchijven(Math.max(0, line.hoeveelheid || 0), line.schijven).regels.map((r) => `${fmtNum(r.hoeveelheid)} × ${fmtEUR(r.prijs)}`).join(' + ')}
                </div>
              )}
              {line.subItems.length > 0 && (
                <details className="mt-1" open={editing && line.subItems.length > 1}>
                  <summary className="text-xs text-secondary cursor-pointer">{line.subItems.length} PDF-item{line.subItems.length === 1 ? '' : 's'}</summary>
                  <div className="mt-2 pl-3 border-l-2 border-lightgrey space-y-1">
                    {line.subItems.map((s) => (
                      <div key={s.parsedItemId} className="text-xs text-secondary flex items-center gap-2 flex-wrap">
                        <span className="flex-1 min-w-[160px]">{s.pdfNaam}{s.matchSource === 'learned' && <span className="chip-neutral ml-2" title="Uit het geheugen: eerder handmatig zo gematcht">geleerd</span>}</span>
                        {editing ? (
                          <>
                            <input type="number" step="0.01" value={s.hoeveelheid}
                              onChange={(e) => updateSub(line.id, s.parsedItemId, { hoeveelheid: parseFloat(e.target.value) || 0 })}
                              className="border border-border rounded px-2 py-0.5 w-20 text-right tabular-nums" />
                            <span>{s.eenheid}×</span>
                            <span>€</span>
                            <input type="number" step="0.01" value={s.prijs}
                              onChange={(e) => updateSub(line.id, s.parsedItemId, { prijs: parseFloat(e.target.value) || 0 })}
                              className="border border-border rounded px-2 py-0.5 w-20 text-right tabular-nums" />
                            <strong>= {fmtEUR(s.subtotaal)}</strong>
                            {onRematch && (
                              <button className="btn-ghost btn-sm" onClick={() => onRematch(line.id, { parsedItemId: s.parsedItemId, pdfNaam: s.pdfNaam, hoeveelheid: s.hoeveelheid, eenheid: s.eenheid })} title="Opnieuw matchen met andere werkpost">↻</button>
                            )}
                            <button className="btn-ghost btn-sm" onClick={() => removeSub(line.id, s.parsedItemId)}>✕</button>
                          </>
                        ) : (
                          <span className="tabular-nums whitespace-nowrap">{fmtNum(s.hoeveelheid)} {s.eenheid} × {fmtEUR(s.prijs)} = <strong>{fmtEUR(s.subtotaal)}</strong></span>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </td>
            <td className="text-right tabular-nums normal-case tracking-normal">{fmtNum(line.hoeveelheid)} {line.eenheid}</td>
            <td className="text-right tabular-nums normal-case tracking-normal">{line.schijven ? <em className="text-secondary">schijven</em> : line.prijs == null ? <em className="text-secondary">varieert</em> : fmtEUR(line.prijs)}</td>
            <td className="text-right tabular-nums font-bold normal-case tracking-normal">
              {vervangenIds?.has(line.id)
                ? <span className="text-secondary font-normal line-through" title="Vervangen door de crepi-ladder">{fmtEUR(line.subtotaal)}</span>
                : line.overlap && line.overlap.status !== 'bevestigd'
                  ? <span className="text-secondary font-normal line-through">{fmtEUR(line.subtotaal)}</span>
                  : fmtEUR(lineSubtotaal(line))}
            </td>
            {editing && (
              <td className="px-3 py-2">
                <OpleveringKeuze
                  oplevering={line.oplevering}
                  onChange={(o) => zetOplevering(line.id, o)}
                  uitgeschakeld={vervangenIds?.has(line.id) ? 'De crepi-ladder rekent op het volledige project; deze post kan niet apart open staan.' : undefined}
                />
              </td>
            )}
            {editing && (
              <td className="text-right whitespace-nowrap">
                <VerschuifKnoppen index={index} aantal={lines.length} onVerschuif={(r) => onUpdate(verschuif(lines, index, r))} />
                <button className="btn-ghost btn-sm" onClick={() => removeLine(line.id)} title="Verwijder hele werkpost">✕</button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MeerwerkLinesTable({ lines, editing, onUpdate, vervangenIds }: {
  lines: MeerwerkLine[];
  editing: boolean;
  onUpdate: (lines: MeerwerkLine[]) => void;
  vervangenIds?: Set<string>;
}) {
  const update = (id: string, patch: Partial<MeerwerkLine>) => onUpdate(lines.map((m) => {
    if (m.id !== id) return m;
    const merged = { ...m, ...patch };
    merged.subtotaal = merged.hoeveelheid * merged.prijs;
    return merged;
  }));
  const remove = (id: string) => onUpdate(lines.filter((m) => m.id !== id));

  if (!lines.length) return editing ? <p className="text-secondary text-sm normal-case tracking-normal">Nog geen meerwerk.</p> : null;

  return (
    <table className="w-full">
      <thead>
        <tr className="thead-row">
          <th className="text-left px-3 py-2">Werk</th>
          <th className="text-right px-3 py-2 w-32">Hoeveelheid</th>
          <th className="text-right px-3 py-2 w-28">Prijs</th>
          <th className="text-right px-3 py-2 w-32">Subtotaal</th>
          {editing && <th className="text-left px-3 py-2 w-44">Oplevering</th>}
          {editing && <th className="w-16"></th>}
        </tr>
      </thead>
      <tbody>
        {lines.map((m, index) => (
          <tr key={m.id} className="border-b border-lightgrey">
            <td className="px-3 py-2 normal-case tracking-normal">
              {editing ? (
                <input className="input" value={m.pdfNaam} onChange={(e) => update(m.id, { pdfNaam: e.target.value })} />
              ) : (
                <><strong>{m.pdfNaam}</strong><OpleveringChip oplevering={m.oplevering} /></>
              )}
              {m.naamArbeid && m.naamArbeid !== 'MEERWERK' && (
                <div className="text-xs text-secondary mt-0.5">→ {m.naamArbeid}</div>
              )}
            </td>
            <td className="text-right tabular-nums normal-case tracking-normal">
              {editing ? (
                <span className="inline-flex items-center gap-1">
                  <input type="number" step="0.01" value={m.hoeveelheid}
                    onChange={(e) => update(m.id, { hoeveelheid: parseFloat(e.target.value) || 0 })}
                    className="border border-border rounded px-2 py-1 w-20 text-right tabular-nums" />
                  <input type="text" value={m.eenheid}
                    onChange={(e) => update(m.id, { eenheid: e.target.value.toUpperCase() })}
                    className="border border-border rounded px-2 py-1 w-14 text-left uppercase" />
                </span>
              ) : (
                <span>{fmtNum(m.hoeveelheid)} {m.eenheid}</span>
              )}
            </td>
            <td className="text-right tabular-nums normal-case tracking-normal">
              {editing ? (
                <input type="number" step="0.01" value={m.prijs}
                  onChange={(e) => update(m.id, { prijs: parseFloat(e.target.value) || 0 })}
                  className="border border-border rounded px-2 py-1 w-24 text-right tabular-nums" />
              ) : (
                fmtEUR(m.prijs)
              )}
            </td>
            <td className="text-right tabular-nums font-bold normal-case tracking-normal">{fmtEUR(m.subtotaal)}</td>
            {editing && (
              <td className="px-3 py-2">
                <OpleveringKeuze
                  oplevering={m.oplevering}
                  onChange={(o) => onUpdate(lines.map((x) => (x.id === m.id ? { ...x, oplevering: o } : x)))}
                  uitgeschakeld={vervangenIds?.has(m.id) ? 'De crepi-ladder rekent op het volledige project; dit meerwerk kan niet apart open staan.' : undefined}
                />
              </td>
            )}
            {editing && (
              <td className="text-right whitespace-nowrap">
                <VerschuifKnoppen index={index} aantal={lines.length} onVerschuif={(r) => onUpdate(verschuif(lines, index, r))} />
                <button className="btn-ghost btn-sm" onClick={() => remove(m.id)}>✕</button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AddPricelistItemRow({ pricelist, onAdd }: {
  pricelist: Pricelist;
  existing: MatchedLine[];
  onAdd: (line: MatchedLine) => void;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [hoeveelheid, setHoeveelheid] = useState(1);
  const items = (pricelist.items ?? []).filter((it) => !it.isNvt && it.naamArbeid !== 'NVT');

  const addLine = () => {
    const item = items.find((it) => it.id === selectedId);
    if (!item || hoeveelheid <= 0) return;
    const newLine: MatchedLine = {
      id: uidLocal('ml'),
      naamArbeid: item.naamArbeid,
      eenheid: item.eenheid,
      prijs: item.prijs,
      hoeveelheid,
      subtotaal: item.prijs * hoeveelheid,
      consumedParsedItemIds: [],
      autoMatched: false,
      ...(item.schijven ? { schijven: item.schijven } : {}),
      subItems: [{
        parsedItemId: uidLocal('pi-manual'),
        pdfNaam: item.pdfNaam + ' (handmatig toegevoegd)',
        plItemId: item.id,
        hoeveelheid,
        eenheid: item.eenheid,
        prijs: item.prijs,
        subtotaal: item.prijs * hoeveelheid,
        confidence: 1,
        autoMatched: false,
        matchSource: 'manual-add',
      }],
    };
    onAdd(newLine);
    setSelectedId('');
    setHoeveelheid(1);
  };

  return (
    <div className="mt-3 pt-3 border-t border-lightgrey">
      <div className="text-xs font-semibold text-secondary mb-2">+ Werkpost toevoegen uit prijslijst</div>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[260px]">
          <PricelistItemPicker
            items={items}
            value={selectedId}
            onChange={setSelectedId}
            placeholder="Zoek arbeid in prijslijst…"
            size="sm"
          />
        </div>
        <input type="number" step="0.01" className="border border-border rounded px-2 py-2 w-24 text-right tabular-nums"
          value={hoeveelheid} onChange={(e) => setHoeveelheid(parseFloat(e.target.value) || 0)} placeholder="hoeveelheid" />
        <button className="btn-ghost btn-sm" onClick={addLine} disabled={!selectedId}>+ Toevoegen</button>
      </div>
    </div>
  );
}

function AddMeerwerkRow({ pricelist, onAdd }: {
  pricelist: Pricelist;
  onAdd: (line: MeerwerkLine) => void;
}) {
  const [mode, setMode] = useState<'pricelist' | 'free'>('pricelist');
  const [selectedId, setSelectedId] = useState('');
  const [hoeveelheid, setHoeveelheid] = useState(1);
  const [omschrijving, setOmschrijving] = useState('');
  const [eenheid, setEenheid] = useState('STUK');
  const [prijs, setPrijs] = useState(0);
  const items = (pricelist.items ?? []).filter((it) => !it.isNvt && it.naamArbeid !== 'NVT');

  const addLine = () => {
    if (mode === 'pricelist') {
      const item = items.find((it) => it.id === selectedId);
      if (!item || hoeveelheid <= 0) return;
      onAdd({
        id: uidLocal('mw'),
        pdfNaam: item.pdfNaam,
        naamArbeid: item.naamArbeid,
        categorie: 'MEERWERK',
        eenheid: item.eenheid,
        hoeveelheid,
        prijs: item.prijs,
        subtotaal: item.prijs * hoeveelheid,
        parsedItemId: uidLocal('pi-mw-manual'),
        plItemId: item.id,
      });
      setSelectedId(''); setHoeveelheid(1);
    } else {
      if (!omschrijving || hoeveelheid <= 0) return;
      onAdd({
        id: uidLocal('mw'),
        pdfNaam: omschrijving,
        naamArbeid: 'MEERWERK',
        categorie: 'MEERWERK',
        eenheid: eenheid.toUpperCase(),
        hoeveelheid,
        prijs,
        subtotaal: prijs * hoeveelheid,
        parsedItemId: uidLocal('pi-mw-manual'),
      });
      setOmschrijving(''); setHoeveelheid(1); setPrijs(0);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-lightgrey">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs font-semibold text-secondary">+ Meerwerk toevoegen</span>
        <button className={`text-xs px-2 py-1 rounded ${mode === 'pricelist' ? 'bg-primary text-white' : 'bg-lightgrey'}`} onClick={() => setMode('pricelist')}>uit prijslijst</button>
        <button className={`text-xs px-2 py-1 rounded ${mode === 'free' ? 'bg-primary text-white' : 'bg-lightgrey'}`} onClick={() => setMode('free')}>vrij invoeren</button>
      </div>
      {mode === 'pricelist' ? (
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex-1 min-w-[260px]">
            <PricelistItemPicker
              items={items}
              value={selectedId}
              onChange={setSelectedId}
              placeholder="Zoek arbeid in prijslijst…"
              size="sm"
            />
          </div>
          <input type="number" step="0.01" className="border border-border rounded px-2 py-2 w-24 text-right tabular-nums"
            value={hoeveelheid} onChange={(e) => setHoeveelheid(parseFloat(e.target.value) || 0)} />
          <button className="btn-ghost btn-sm" onClick={addLine} disabled={!selectedId}>+ Toevoegen</button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 items-center">
          <input type="text" className="input flex-1 min-w-[200px]" placeholder="Omschrijving meerwerk…"
            value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} />
          <input type="number" step="0.01" className="border border-border rounded px-2 py-2 w-24 text-right tabular-nums"
            placeholder="aantal" value={hoeveelheid} onChange={(e) => setHoeveelheid(parseFloat(e.target.value) || 0)} />
          <input type="text" className="border border-border rounded px-2 py-2 w-16 text-left uppercase"
            value={eenheid} onChange={(e) => setEenheid(e.target.value)} />
          <span>×</span>
          <span>€</span>
          <input type="number" step="0.01" className="border border-border rounded px-2 py-2 w-24 text-right tabular-nums"
            placeholder="prijs" value={prijs} onChange={(e) => setPrijs(parseFloat(e.target.value) || 0)} />
          <button className="btn-ghost btn-sm" onClick={addLine} disabled={!omschrijving || hoeveelheid <= 0}>+ Toevoegen</button>
        </div>
      )}
    </div>
  );
}

function RematchRow({ item, pricelistItems, onResolve, onCancel }: {
  item: RematchItem;
  pricelistItems: PricelistItem[];
  onResolve: (plItem: PricelistItem) => void;
  onCancel: () => void;
}) {
  const [selectedId, setSelectedId] = useState('');
  const selectedItem = pricelistItems.find((it) => it.id === selectedId);

  return (
    <div className="bg-white rounded-md shadow-xs">
      <div className="px-4 py-2 bg-warning-100 border-l-4 border-warning flex items-center gap-3 flex-wrap">
        <span className="flex-1 font-semibold text-sm">{item.pdfNaam}</span>
        <span className="text-xs text-secondary">{fmtNum(item.hoeveelheid)} {item.eenheid}</span>
      </div>
      <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <PricelistItemPicker
            items={pricelistItems}
            value={selectedId}
            onChange={setSelectedId}
            placeholder="Zoek werkpost in prijslijst…"
            size="sm"
          />
        </div>
        <button
          className="btn-primary btn-sm"
          disabled={!selectedItem}
          onClick={() => selectedItem && onResolve(selectedItem)}
        >
          ✓ Match
        </button>
        <button className="btn-ghost btn-sm" onClick={onCancel}>Annuleer</button>
      </div>
    </div>
  );
}

function PendingRow({ pending: p, pricelistItems, onMatch, onMeerwerk, onSkip }: {
  pending: PendingItem;
  pricelistItems: PricelistItem[];
  onMatch: (plItemId: string) => void;
  onMeerwerk: (prijs: number) => void;
  onSkip: () => void;
}) {
  const [mode, setMode] = useState<'pick' | 'meerwerk'>('pick');
  const [plItemId, setPlItemId] = useState(p.suggestions[0]?.item.id ?? '');
  const [mwPrijs, setMwPrijs] = useState(0);

  return (
    <div className="bg-white rounded-md shadow-xs">
      <div className="px-4 py-2 bg-warning-100 border-l-4 border-warning flex items-center gap-3 flex-wrap">
        <span className="flex-1 font-semibold text-sm">{p.parsedItem.werken}</span>
        <span className="text-xs text-secondary">{fmtNum(p.parsedItem.werkelijkeHoeveelheid)} {p.parsedItem.eenheid}</span>
        {p.kind === 'review' && p.suggestions[0] && (
          <span className="text-xs text-secondary">Beste suggestie: {(p.suggestions[0].score * 100).toFixed(0)}%</span>
        )}
      </div>
      <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
        <div className="flex gap-1 text-xs">
          <button className={`px-2 py-1 rounded ${mode === 'pick' ? 'bg-primary text-white' : 'bg-warmgrey'}`} onClick={() => setMode('pick')}>Match arbeid</button>
          <button className={`px-2 py-1 rounded ${mode === 'meerwerk' ? 'bg-accent text-white' : 'bg-warmgrey'}`} onClick={() => setMode('meerwerk')}>Meerwerk</button>
        </div>
        {mode === 'pick' ? (
          <>
            <div className="flex-1 min-w-[260px]">
              <PricelistItemPicker
                items={pricelistItems}
                suggestions={p.suggestions}
                value={plItemId}
                onChange={setPlItemId}
                placeholder="Zoek arbeid…"
                size="sm"
              />
            </div>
            <button className="btn-primary btn-sm" disabled={!plItemId} onClick={() => onMatch(plItemId)}>✓ Match</button>
          </>
        ) : (
          <>
            <span className="text-xs text-secondary">Meerwerk-prijs:</span>
            <input type="number" step="0.01" min="0" value={mwPrijs} onChange={(e) => setMwPrijs(parseFloat(e.target.value) || 0)} className="border border-border rounded px-2 py-1 w-24 text-right tabular-nums" placeholder="0.00" />
            <span className="text-xs">€/{p.parsedItem.eenheid || 'eenheid'}</span>
            <button className="btn-accent btn-sm" disabled={mwPrijs <= 0} onClick={() => onMeerwerk(mwPrijs)}>+ Meerwerk</button>
          </>
        )}
        <button className="btn-ghost btn-sm" onClick={onSkip}>Negeer</button>
      </div>
    </div>
  );
}

/** Leesbare foutmelding uit een API-fout. */
function foutTekst(e: unknown): string {
  if (e instanceof ApiError && e.payload && typeof e.payload === 'object' && 'error' in e.payload) {
    const p = e.payload as { error: string; details?: unknown };
    return Array.isArray(p.details) ? `${p.error}: ${p.details.join('; ')}` : p.error;
  }
  return e instanceof Error ? e.message : 'onbekende fout';
}
